import React, { PropsWithChildren, FC, useCallback, useMemo, useState } from "react";

import {
    Accordion,
    Button,
    ButtonProps,
    Dialog,
    FieldCheckbox,
    LoadingSpinner
} from "@xapp/components";
import { useBoolState } from "utils/bool";

export interface GoogleBusinessProfileQuestionStepProps {
    /**
     * Does the user have a website, if passed through it will give a disclaimer about not having a website & not having a profile.
     */
    hasWebsite?: boolean;
    /**
     * Optional style applied to the root div
     */
    style?: React.CSSProperties;
    /**
     * Optional class name to apply to the root div
     */
    className?: string;
    /**
     * Updating status
     */
    updating?: boolean;
    /**
     * Status message to display
     */
    updatingStatusMessage?: string;
    /**
     * Error message to display to the user if something goes wrong.
     */
    error?: { error: string, message: string };
    /**
     * The user has clicked next
     */
    onNext(hasProfile: boolean): void;
    /**
     * Optional, user clicks back button
     */
    onBack?(): void;
}

export const GoogleBusinessProfileQuestionStep: FC<PropsWithChildren<GoogleBusinessProfileQuestionStepProps>> = props => {

    const { style, className, onNext, updating: analyzing, hasWebsite, onBack } = props;

    const [displayDisclaimerDialog, hideDisclaimerDialog, showDisclaimerDialog] = useBoolState(false);
    const [displayLearnMore, hideLearnMore, showLearnMore] = useBoolState(false);

    const [hasProfile, setHasProfile] = useState<boolean>();

    const handleNextClick = () => {
        // if they don't have a website & don't have a profile, we need to let them know that they will need one or the other
        // and we can't provide much value until they have one.
        if (typeof hasWebsite === "boolean" && !hasWebsite && typeof hasProfile === "boolean" && !hasProfile) {
            // display the dialog
            showDisclaimerDialog();
        } else {
            onNext(hasProfile);
        }
    };

    const handleDisclaimerAccept = useCallback(() => {
        hideDisclaimerDialog();
        onNext(hasProfile);
    }, [hasProfile, hideDisclaimerDialog, onNext]);

    const handleHasProfileChange = useCallback((value: boolean) => {
        setHasProfile(value);
    }, []);

    const handleNoProfileChange = useCallback((value: boolean) => {
        setHasProfile(!value);
    }, []);

    const noProfile: boolean = useMemo(() => {
        return typeof hasProfile === "boolean" ? !hasProfile : false;
    }, [hasProfile]);

    if (analyzing) {
        return (
            <div style={{ ...style }} className={className}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
                    <LoadingSpinner />
                    <h2>{props.updatingStatusMessage}</h2>
                </div>
            </div>
        );
    }

    const actions: ButtonProps[] = [
        {
            label: "Done",
            onClick: hideLearnMore,
            variation: "primary"
        }
    ];

    const canClickNext: boolean = typeof hasProfile === "boolean";

    return (
        <>
            <div style={{ ...style }} className={className}>
                <div>
                    <h2>Do you have a Google Business Profile?</h2>
                    <p>It is required for Reserve with Google integration where we can help you capture leads and bookings from Google Search & Maps by adding a "Book Online" button.</p>
                    <div style={{ display: "flex", alignContent: "center", justifyContent: "center", marginTop: "5px" }}>
                        <small>What is a Google Business Profile? <a onClick={showLearnMore} href={"#learn-more"}>Learn More</a></small>
                    </div>
                    <div style={{ padding: "20px 0px 50px 0px" }}>
                        <Accordion
                            title={(
                                <div style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "center",
                                    alignContent: "center",
                                    alignItems: "center"
                                }}>
                                    <FieldCheckbox
                                        fieldStyle={{ marginBottom: "2px" }}
                                        onChange={handleHasProfileChange}
                                        value={typeof hasProfile === "boolean" ? hasProfile : false}
                                    />
                                    <h3>I have a Google Business Profile (recommended)</h3>
                                </div>
                            )} onChange={(event, expanded) => {
                                // event.preventDefault();
                                handleHasProfileChange(expanded)
                            }}
                            disableExpandIcon={true}
                            expanded={typeof hasProfile === "boolean" ? hasProfile : false}>
                            <div>
                                <div style={{ paddingBottom: "20px" }}><p>Great! We will try to find it on the next step.</p></div>
                            </div>
                        </Accordion>
                        <Accordion
                            title={(
                                <div style={{ display: "flex", flexDirection: "row", alignContent: "center", alignItems: "center" }}>
                                    <FieldCheckbox
                                        fieldStyle={{ marginBottom: "2px" }}
                                        onChange={handleNoProfileChange}
                                        value={noProfile}
                                    />
                                    <h3>I don't have a Google Business Profile</h3>
                                </div>
                            )} onChange={(event, expanded) => {
                                // event.preventDefault();
                                handleNoProfileChange(expanded)
                            }}
                            disableExpandIcon={true}
                            expanded={typeof noProfile === "boolean" ? noProfile : false}>
                            <div style={{ paddingBottom: "20px" }}>
                                {/* TODO: Change it so mobile is new tab & desktop is modal */}
                                <p>No problem, you can always add it later once you create it.  Create one <a target={"_new"} href="https://www.google.com/business/">here.</a></p>
                            </div>
                        </Accordion>
                    </div>
                    <div style={{ padding: "20px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: typeof onBack === "function" ? "space-between" : "right" }}>
                        {typeof onBack === "function" && (
                            <Button
                                leftIcon="arrow_back"
                                label={`Back`}
                                variation="secondary"
                                onClick={onBack}
                            />
                        )}
                        <Button
                            rightIcon="arrow_forward"
                            label={`Next`}
                            variation="primary"
                            onClick={handleNextClick}
                            disabled={!canClickNext}
                        />
                    </div>
                </div >
            </div >
            {/* Dialogs! */}
            <Dialog
                active={displayDisclaimerDialog}
                actions={[{ label: "I Understand", onClick: handleDisclaimerAccept, variation: "primary" }]}
                title="Important!">
                <p>In order to get the most value out of our service, you will <b>need at least a website or a Google Business Profile.</b></p>
                <br />
                <p> You <b>can proceed</b> with the intention of adding these later.</p>
            </Dialog>

            <Dialog
                active={displayLearnMore}
                actions={actions}
                title="What is Google Business Profile?">
                <p>A Google Business Profile is either created automatically by Google or by the business owner.  It allows the business to better communicate information in Google's Search & Map results.</p>
                <div style={{ width: "100%" }}>
                    <img src="/img/channels/google-business-profile.png" alt="Google Business Profile" style={{ width: "100%", height: "auto" }} />
                </div>
            </Dialog>
        </>
    );
}